import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Route, Clock, MapPin, Mountain, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { api } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

const ItinerariesPage = () => {
  const { t, getLocalizedField, language } = useLanguage();
  const navigate = useNavigate();
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadItineraries();
  }, []);

  const loadItineraries = async () => {
    try {
      const res = await api.getItineraries();
      setItineraries(res.data);
    } catch (error) {
      console.error('Error loading itineraries:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      easy: 'bg-green-100 text-green-700',
      medium: 'bg-yellow-100 text-yellow-700',
      hard: 'bg-red-100 text-red-700',
    };
    return colors[difficulty] || colors.medium;
  };

  const getDifficultyLabel = (difficulty) => {
    const labels = {
      easy: { es: 'Fácil', en: 'Easy', de: 'Einfach' },
      medium: { es: 'Moderado', en: 'Moderate', de: 'Mittel' },
      hard: { es: 'Difícil', en: 'Difficult', de: 'Schwer' },
    };
    return labels[difficulty]?.[language] || labels.medium.es;
  };

  if (loading) {
    return (
      <div className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-12 w-64 mb-4" />
          <Skeleton className="h-6 w-96 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-72 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20 min-h-screen" data-testid="itineraries-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#38B2AC] to-[#005A63] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Route size={24} />
            </div>
            <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold">
              Itinerarios recomendados
            </h1>
          </div>
          <p className="text-white/80 text-lg max-w-2xl">
            Rutas diseñadas por locales para que descubras lo mejor de Tenerife. 
            Sigue cada parada con tu GPS y disfruta de la aventura.
          </p>
        </div>
      </div>

      {/* Itineraries grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {itineraries.length === 0 ? (
          <div className="text-center py-16">
            <Route size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No hay itinerarios disponibles</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {itineraries.map((itinerary) => (
              <ItineraryCard
                key={itinerary.id}
                itinerary={itinerary}
                onClick={() => navigate(`/itinerary/${itinerary.id}`)}
                getDifficultyColor={getDifficultyColor}
                getDifficultyLabel={getDifficultyLabel}
                language={language}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ItineraryCard = ({ itinerary, onClick, getDifficultyColor, getDifficultyLabel, language }) => {
  const { getLocalizedField } = useLanguage();
  
  const name = getLocalizedField(itinerary, 'name');
  const description = getLocalizedField(itinerary, 'description');

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group"
      onClick={onClick}
      data-testid={`itinerary-card-${itinerary.id}`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={itinerary.image}
          alt={name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        {/* Difficulty badge */}
        <Badge className={`absolute top-4 right-4 ${getDifficultyColor(itinerary.difficulty)}`}>
          <Mountain size={14} className="mr-1" />
          {getDifficultyLabel(itinerary.difficulty)}
        </Badge>
        
        {/* Stops count */}
        <div className="absolute bottom-4 left-4 text-white">
          <p className="text-sm opacity-80">{itinerary.stops?.length || 0} paradas</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1D1D1F] mb-2 group-hover:text-[#38B2AC] transition-colors">
          {name}
        </h3>
        
        <p className="text-gray-500 text-sm line-clamp-2 mb-4">
          {description}
        </p>

        {/* Meta info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 text-gray-600">
              <Clock size={16} />
              <span className="text-sm">{itinerary.duration}</span>
            </div>
            <div className="flex items-center gap-1 text-gray-600">
              <MapPin size={16} />
              <span className="text-sm">{itinerary.distance_km} km</span>
            </div>
          </div>
          
          <div className="w-8 h-8 bg-[#38B2AC]/10 rounded-full flex items-center justify-center group-hover:bg-[#38B2AC] transition-colors">
            <ArrowRight size={16} className="text-[#38B2AC] group-hover:text-white transition-colors" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItinerariesPage;
